// The board (spec §8.4, §14.3): SVG, crisp at any size, hop-by-hop capture
// animation, fading captured pieces, arrows and a soft glow when solved.
//
// Move input follows spec/move-input-spec.md and lives in `moveInput.ts`:
// beginner or advanced assistance, clicking with or without dragging, and the
// 0.5 s grace period in which a slip can be replaced. This file only turns
// pointer and keyboard events into its calls, and its effects into pixels.
//
// The board never decides what is legal: it only offers the `legal` moves it
// is given (from the API, or the WASM generator).
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { LegalMove } from "../api/client";
import { useT } from "../i18n";
import { apply, centre, parse, squareAt, type PieceKind } from "../lib/textfen";
import { MoveInput, type Assist, type Effect, type HintCode, type View } from "./moveInput";
import "./board.css";

export type Arrow = { from: number; to: number; path?: number[]; color?: string; width?: number; opacity?: number };

export type BoardHandle = {
  /** Animate a move from the board's current position (black replies, replays). */
  play: (move: LegalMove) => Promise<void>;
  /** A soft glow on the board edge. */
  glow: (kind: "success" | "error") => void;
  /** Resolves when every queued animation has finished. */
  idle: () => Promise<void>;
};

/** What the board tells its owner when a move counts (after the grace period). */
export type MoveMeta = { enteredAt: number; replaced: string[] };

type Props = {
  position: string;
  legal?: LegalMove[];
  onMove?: (move: LegalMove, meta: MoveMeta) => void;
  lastMove?: { from: number; to: number } | null;
  arrows?: Arrow[];
  coordinates?: boolean;
  flipped?: boolean;
  label?: string;
  /** move-input-spec §1 */
  assist?: Assist;
  drag?: boolean;
  showTargets?: boolean;
};

type BoardPiece = { id: number; sq: number; kind: PieceKind; dying?: number };
type DragState = { x: number; y: number; sx: number; sy: number; active: boolean; zone: number | null; touch: boolean };

const HOP_MS = 140;
const SLIDE_MS = 180;
// every hop of a capture eases in and out, so the piece glides from square to square
const HOP_EASE = "cubic-bezier(.45,0,.25,1)";
const SLIDE_EASE = "cubic-bezier(.25,.8,.25,1)";
const CAPTURE_MS = 180;
const FLY_MS = 150;
const HINT_MS = 2500;
let nextId = 1;

const EMPTY_VIEW: View = { selected: null, at: null, origin: null, prefix: [], faded: [], dots: [], rings: [], outlines: [], lines: [] };

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function toPieces(textfen: string, previous: BoardPiece[] = []): BoardPiece[] {
  const left = new Map(previous.filter((p) => p.dying === undefined).map((p) => [`${p.sq}${p.kind}`, p.id]));
  return parse(textfen).map((p) => ({ ...p, id: left.get(`${p.sq}${p.kind}`) ?? nextId++ }));
}

/** Which hop of `path` passes over `sq` (for the capture timing). */
function hopOver(path: number[], sq: number, flipped: boolean): number {
  const [x, y] = centre(sq, flipped);
  for (let k = 0; k + 1 < path.length; k++) {
    const [x1, y1] = centre(path[k], flipped);
    const [x2, y2] = centre(path[k + 1], flipped);
    const within = (x - x1) * (x - x2) <= 0 && (y - y1) * (y - y2) <= 0;
    const collinear = Math.abs((x2 - x1) * (y - y1) - (y2 - y1) * (x - x1)) < 1;
    if (within && collinear) return k;
  }
  return path.length - 2;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const translate = ([x, y]: [number, number]) => ({ transform: `translate(${x - 50}px, ${y - 50}px)` });
/** Keyframes for a path; with `ease`, each hop eases on its own. */
const frames = (points: [number, number][], ease?: string) =>
  points.map((p) => (ease ? { ...translate(p), easing: ease } : translate(p)));

export const Board = forwardRef<BoardHandle, Props>(function Board(
  {
    position,
    legal = [],
    onMove,
    lastMove,
    arrows = [],
    coordinates = false,
    flipped = false,
    label,
    assist = "advanced",
    drag: dragOn = true,
    showTargets = true,
  },
  ref,
) {
  const t = useT();
  const [pieces, setPiecesState] = useState<BoardPiece[]>(() => toPieces(position));
  const piecesRef = useRef(pieces);
  const setPieces = useCallback((fn: (prev: BoardPiece[]) => BoardPiece[]) => {
    piecesRef.current = fn(piecesRef.current);
    setPiecesState(piecesRef.current);
  }, []);
  const internal = useRef(position);
  const animating = useRef(false);
  // replies and position changes run one after the other, in call order
  const queue = useRef<Promise<void>>(Promise.resolve());
  // the user's own move, shown during the grace period; a take-back cuts it off
  const userAnim = useRef<Promise<void>>(Promise.resolve());
  const userGen = useRef(0);
  const els = useRef(new Map<number, SVGGElement>());
  const svg = useRef<SVGSVGElement>(null);
  const [view, setViewState] = useState<View>(EMPTY_VIEW);
  const viewRef = useRef<View>(EMPTY_VIEW);
  const setView = useCallback((v: View) => {
    viewRef.current = v;
    setViewState(v);
  }, []);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const movingId = useRef<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [pulse, setPulse] = useState<number[]>([]);
  const [hint, setHint] = useState<{ code: HintCode; n?: number; key: number } | null>(null);
  const [shake, setShake] = useState(0);
  const [glow, setGlow] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const walked = useRef<number[]>([]);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  const interactive = legal.length > 0 && !!onMove;
  const input = useMemo(
    () => (interactive ? new MoveInput(position, legal, assist) : null),
    // a new position or a new list of legal moves starts a fresh input
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [position, legal, assist, interactive],
  );
  const inputRef = useRef(input);
  inputRef.current = input;
  useEffect(() => {
    setView(EMPTY_VIEW);
    walked.current = [];
    setDrag(null);
    dragRef.current = null;
    setTyped("");
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [input, setView]);

  // follow the `position` prop, after any running animation; a position the
  // board reached by animating is already there
  useEffect(() => {
    queue.current = queue.current
      .then(() => userAnim.current)
      .then(() => {
        if (position === internal.current) return;
        internal.current = position;
        setPieces((prev) => toPieces(position, prev));
      });
  }, [position, setPieces]);

  const movers = useMemo(() => new Set(legal.map((m) => m.from)), [legal]);

  /** Animate `move` along `route` from `route[from]` (or from `start`). */
  const run = useCallback(
    async (move: LegalMove, opts: { route?: number[]; from?: number; start?: [number, number]; faded?: number[]; gen?: number } = {}) => {
      const user = opts.gen !== undefined;
      if (!user) animating.current = true;
      const mover = piecesRef.current.find((p) => p.sq === move.from && p.dying === undefined);
      const el = mover ? els.current.get(mover.id) : undefined;
      const fast = reducedMotion();
      const route = opts.route ?? (move.path.length >= 2 ? move.path : [move.from, move.to]);
      const from = opts.from ?? 0;
      const rest = route.slice(from + 1);
      const points: [number, number][] = [opts.start ?? centre(route[from], flipped), ...rest.map((s) => centre(s, flipped))];
      const hop = route.length > 2 ? HOP_MS : SLIDE_MS;
      const duration = fast ? 1 : hop * Math.max(0, points.length - 1);

      // captured pieces fade as the moving piece passes them; those already
      // faded during input go at once
      const fadeAt = new Map<number, number>();
      for (const c of move.captured) {
        const k = opts.faded?.includes(c) ? 0 : Math.max(0, hopOver(route, c, flipped) - from);
        fadeAt.set(c, fast || opts.faded?.includes(c) ? 0 : Math.round((k + 0.6) * hop));
      }
      setPieces((prev) =>
        prev.map((p) => (fadeAt.has(p.sq) && p.id !== mover?.id && p.dying === undefined ? { ...p, dying: fadeAt.get(p.sq)! } : p)),
      );

      if (el && points.length > 1 && duration > 1) {
        const anim = el.animate(frames(points, hop === SLIDE_MS ? undefined : HOP_EASE), {
          duration,
          easing: hop === SLIDE_MS ? SLIDE_EASE : "linear",
          fill: "forwards",
        });
        // a paused timeline (hidden tab) must not hold the game up
        await Promise.race([anim.finished.catch(() => undefined), sleep(duration + 100)]);
      }
      if (user && opts.gen !== userGen.current) return;
      internal.current = apply(internal.current, move);
      setPieces((prev) =>
        prev.map((p) =>
          p.id === mover?.id ? { ...p, sq: move.to, kind: (move.promotion ? p.kind.toUpperCase() : p.kind) as PieceKind } : p,
        ),
      );
      // the WAAPI fill is dropped once React has placed the piece on its square
      setTimeout(() => el?.getAnimations().forEach((a) => a.cancel()), 20);
      const longest = Math.max(0, ...fadeAt.values()) + (fast ? 80 : CAPTURE_MS);
      await sleep(Math.max(0, longest - duration));
      if (user && opts.gen !== userGen.current) return;
      setPieces((prev) => prev.filter((p) => p.dying === undefined));
      if (!user) animating.current = false;
    },
    [flipped, setPieces],
  );

  const animate = useCallback(
    (move: LegalMove) => {
      queue.current = queue.current.then(() => run(move));
      return queue.current;
    },
    [run],
  );

  useImperativeHandle(
    ref,
    () => ({
      play: (move) => animate(move),
      idle: () => Promise.all([queue.current, userAnim.current]).then(() => undefined),
      glow: (kind) => {
        setGlow(kind);
        setTimeout(() => setGlow(null), 700);
      },
    }),
    [animate],
  );

  // -- effects of the move input ----------------------------------------------

  const takeBack = useCallback(() => {
    userGen.current++;
    for (const el of els.current.values()) el.getAnimations().forEach((a) => a.cancel());
    internal.current = position;
    setPieces(() => toPieces(position, piecesRef.current.filter((p) => p.dying === undefined)));
    userAnim.current = Promise.resolve();
  }, [position, setPieces]);

  /** The dragged piece flies from where it was dropped back to `to`. */
  const flyBack = useCallback(
    (from: [number, number] | null, pieceSq: number | null, to: number | null) => {
      if (!from || pieceSq === null || to === null) return;
      const mover = piecesRef.current.find((p) => p.sq === pieceSq && p.dying === undefined);
      const el = mover ? els.current.get(mover.id) : undefined;
      if (!el || reducedMotion()) return;
      el.animate([translate(from), translate(centre(to, flipped))], { duration: FLY_MS, easing: "ease-out" });
    },
    [flipped],
  );

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    const mi = inputRef.current;
    const at = mi?.deadline();
    if (!mi || at === null || at === undefined) return;
    timer.current = setTimeout(() => {
      if (inputRef.current !== mi) return;
      handleRef.current(mi.tick(performance.now()));
    }, Math.max(0, at - performance.now()) + 5);
  }, []);

  const handle = useCallback(
    (effects: Effect[], dropped: [number, number] | null = null) => {
      const mi = inputRef.current;
      if (!mi) return;
      const before = viewRef.current;
      const after = mi.view();
      for (const fx of effects) {
        if (fx.kind === "hint") {
          const key = Date.now();
          setHint({ code: fx.code, n: fx.n, key });
          setTimeout(() => setHint((h) => (h?.key === key ? null : h)), HINT_MS);
        } else if (fx.kind === "pulse") {
          setPulse(fx.squares);
          setTimeout(() => setPulse([]), 700);
        } else if (fx.kind === "shake") setShake((s) => s + 1);
        else if (fx.kind === "takeBack") takeBack();
        else if (fx.kind === "flyBack") flyBack(dropped, after.origin ?? before.origin, after.at ?? after.origin ?? before.origin);
        else if (fx.kind === "show") {
          const gen = ++userGen.current;
          const start = dropped && fx.route[fx.from] !== undefined ? dropped : undefined;
          userAnim.current = run(fx.move, { route: fx.route, from: fx.from, start, faded: before.faded, gen });
        } else if (fx.kind === "commit") onMoveRef.current?.(fx.move, { enteredAt: fx.enteredAt, replaced: fx.replaced });
      }
      setView(after);
      schedule();
    },
    [takeBack, flyBack, run, schedule, setView],
  );
  const handleRef = useRef(handle);
  handleRef.current = handle;

  // the piece walks along the squares a click added (beginner step, advanced walk)
  useLayoutEffect(() => {
    const prev = walked.current;
    walked.current = view.prefix;
    if (drag?.active || view.prefix.length < 2 || reducedMotion()) return;
    const grows = prev.length >= 1 && prev.length < view.prefix.length && prev.every((s, i) => view.prefix[i] === s);
    if (!grows) return;
    const mover = piecesRef.current.find((p) => p.sq === view.prefix[0] && p.dying === undefined);
    const el = mover ? els.current.get(mover.id) : undefined;
    const path = view.prefix.slice(prev.length - 1);
    el?.animate(frames(path.map((s) => centre(s, flipped)), HOP_EASE), {
      duration: HOP_MS * (path.length - 1),
      easing: "linear",
    });
  }, [view.prefix, drag?.active, flipped]);

  // -- pointer and keyboard ----------------------------------------------------

  function point(e: ReactPointerEvent): [number, number] {
    const rect = svg.current!.getBoundingClientRect();
    return [((e.clientX - rect.left) / rect.width) * 1000, ((e.clientY - rect.top) / rect.height) * 1000];
  }
  const squareOf = ([x, y]: [number, number]) => squareAt(Math.floor(y / 100), Math.floor(x / 100), flipped);
  /** The square whose centre zone (the middle 60 %) the point is in (§4). */
  const zoneOf = ([x, y]: [number, number]) => {
    const fx = x % 100;
    const fy = y % 100;
    return fx >= 20 && fx <= 80 && fy >= 20 && fy <= 80 ? squareOf([x, y]) : null;
  };

  function onDown(e: ReactPointerEvent) {
    const mi = inputRef.current;
    if (!mi || animating.current || e.button === 2) return;
    const p = point(e);
    const sq = squareOf(p);
    if (sq === null) return;
    const now = performance.now();
    if (dragOn && mi.draggable(sq)) {
      const d: DragState = { x: p[0], y: p[1], sx: p[0], sy: p[1], active: false, zone: sq, touch: e.pointerType !== "mouse" };
      dragRef.current = d;
      setDrag(d);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      handle(mi.press(sq, now));
    } else handle(mi.click(sq, now));
  }

  /** The dragged piece follows the pointer without a React render. */
  function placeDragged(d: DragState) {
    const el = movingId.current !== null ? els.current.get(movingId.current) : undefined;
    if (el) el.style.transform = `translate(${d.x - 50}px, ${d.y - 50 - (d.touch ? 40 : 0)}px)`;
  }

  function onMovePointer(e: ReactPointerEvent) {
    const mi = inputRef.current;
    const d = dragRef.current;
    if (!d) {
      // not dragging: light up the target square under the pointer
      const sq = mi ? squareOf(point(e)) : null;
      if (sq !== hover) setHover(sq);
      return;
    }
    if (!mi) return;
    const p = point(e);
    const rect = svg.current!.getBoundingClientRect();
    const px = (Math.hypot(p[0] - d.sx, p[1] - d.sy) * rect.width) / 1000;
    let next: DragState = { ...d, x: p[0], y: p[1] };
    const effects: Effect[] = [];
    if (!d.active && px > (d.touch ? 10 : 4)) {
      next = { ...next, active: true };
      mi.dragStart();
    }
    if (next.active) {
      const z = zoneOf(p);
      if (z !== null && z !== d.zone) {
        next = { ...next, zone: z };
        effects.push(...mi.enter(z, performance.now()));
      }
    }
    dragRef.current = next;
    if (next.active) placeDragged(next);
    const sq = squareOf(p);
    if (sq !== hover) setHover(sq);
    // a render only when something besides the position changed
    if (next.active !== d.active || next.zone !== d.zone) setDrag(next);
    if (effects.length || next.active !== d.active) handle(effects);
  }

  function onUp(e: ReactPointerEvent) {
    const mi = inputRef.current;
    const d = dragRef.current;
    if (!mi || !d) return;
    const p = point(e);
    dragRef.current = null;
    setDrag(null);
    handle(mi.release(squareOf(p), performance.now()), d.active ? p : null);
  }

  function onCancel() {
    const mi = inputRef.current;
    const d = dragRef.current;
    if (!mi || !d) return;
    dragRef.current = null;
    setDrag(null);
    handle(mi.cancelDrag(performance.now()), d.active ? [d.x, d.y] : null);
  }

  function onKey(e: ReactKeyboardEvent) {
    const mi = inputRef.current;
    if (!mi) return;
    const now = performance.now();
    if (e.key === "Escape" || e.key === "Backspace") {
      if (typed && e.key === "Backspace") setTyped(typed.slice(0, -1));
      else {
        setTyped("");
        handle(mi.key(e.key, now));
      }
      e.preventDefault();
    } else if (/^[0-9x-]$/.test(e.key)) {
      setTyped((s) => (s + e.key).slice(0, 24));
      e.preventDefault();
      e.stopPropagation(); // not the trainer's shortcuts
    } else if (e.key === "Enter" && typed) {
      handle(mi.typed(typed, now));
      setTyped("");
      e.preventDefault();
    }
  }

  // -- drawing -------------------------------------------------------------------

  const squares = [];
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 10; c++) {
      const sq = squareAt(r, c, flipped);
      const dark = sq !== null;
      const last = lastMove && sq !== null && (sq === lastMove.from || sq === lastMove.to);
      squares.push(
        <rect key={`${r}-${c}`} x={c * 100} y={r * 100} width={100} height={100} className={dark ? "sq dark" : "sq light"} />,
      );
      if (last) squares.push(<rect key={`l${sq}`} x={c * 100} y={r * 100} width={100} height={100} className="sq last" />);
      if (sq !== null && sq === view.selected)
        squares.push(<rect key={`s${sq}`} x={c * 100} y={r * 100} width={100} height={100} className="sq selected" />);
      if (coordinates && sq !== null)
        squares.push(
          <text key={`t${sq}`} x={c * 100 + 6} y={r * 100 + 22} className="coord">
            {sq}
          </text>,
        );
    }
  }

  const moving = view.origin !== null ? pieces.find((p) => p.sq === view.origin && p.dying === undefined) : undefined;
  movingId.current = moving?.id ?? null;
  const targets = showTargets ? [...view.dots.map((sq) => [sq, false] as const), ...view.rings.map((sq) => [sq, true] as const)] : [];
  const faded = new Set(view.faded);
  const hintText = hint
    ? t(`move.${hint.code}`, hint.n !== undefined ? { n: hint.n } : undefined)
    : null;

  return (
    <div className="board-outer">
      <div
        className={`board-wrap ${glow ? `glow-${glow}` : ""}`}
        tabIndex={interactive ? 0 : -1}
        onKeyDown={onKey}
        aria-label={label ?? "draughts board"}
      >
        <svg
          ref={svg}
          viewBox="0 0 1000 1000"
          className={`board ${interactive ? "interactive" : ""}`}
          role="img"
          aria-label={label ?? "draughts board"}
          onPointerDown={onDown}
          onPointerMove={onMovePointer}
          onPointerUp={onUp}
          onPointerCancel={onCancel}
          onPointerLeave={() => !dragRef.current && setHover(null)}
          onLostPointerCapture={() => dragRef.current && onCancel()}
          onContextMenu={(e) => {
            e.preventDefault();
            const mi = inputRef.current;
            if (mi) handle(mi.key("Backspace", performance.now()));
          }}
        >
          <defs>
            <radialGradient id="pw" cx="40%" cy="35%" r="70%">
              <stop offset="0%" stopColor="var(--piece-white)" />
              <stop offset="100%" stopColor="color-mix(in srgb, var(--piece-white) 85%, #8a7f6c)" />
            </radialGradient>
            <radialGradient id="pb" cx="40%" cy="35%" r="70%">
              <stop offset="0%" stopColor="color-mix(in srgb, var(--piece-black) 80%, #6b5f57)" />
              <stop offset="100%" stopColor="var(--piece-black)" />
            </radialGradient>
            <marker id="arrowhead" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3" markerHeight="3" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
            </marker>
          </defs>
          {squares}
          {pulse.map((sq) => {
            const [x, y] = centre(sq, flipped);
            return <circle key={`h${sq}`} cx={x} cy={y} r={46} className="hint" />;
          })}
          {targets.map(([sq, dest]) => {
            const [x, y] = centre(sq, flipped);
            return (
              <rect
                key={`t${sq}`}
                x={x - 47}
                y={y - 47}
                width={94}
                height={94}
                rx={6}
                className={`target ${dest ? "dest" : ""} ${sq === hover ? "hover" : ""}`}
              />
            );
          })}
          {view.lines.map((line, i) => (
            <polyline
              key={`route${i}`}
              points={line.map((s) => centre(s, flipped).join(",")).join(" ")}
              className={`route ${i % 2 ? "alt" : ""}`}
            />
          ))}
          {view.outlines.map((sq) => {
            const [x, y] = centre(sq, flipped);
            return <circle key={`o${sq}`} cx={x} cy={y} r={44} className="outline" />;
          })}
          {pieces.map((p) => {
            const isMoving = moving?.id === p.id;
            const live = dragRef.current;
            const dragging = isMoving && !!drag?.active && !!live?.active;
            const at = isMoving && view.at !== null ? view.at : p.sq;
            const [x, y] = dragging ? [live!.x, live!.y - (live!.touch ? 40 : 0)] : centre(at, flipped);
            const white = p.kind === "w" || p.kind === "W";
            const king = p.kind === "W" || p.kind === "B";
            const dying = p.dying !== undefined;
            return (
              <g
                key={p.id}
                ref={(el) => {
                  if (el) els.current.set(p.id, el);
                  else els.current.delete(p.id);
                }}
                className={`piece ${dragging ? "dragging" : ""} ${interactive && movers.has(p.sq) ? "movable" : ""} ${isMoving && shake ? `shake-${shake % 2}` : ""}`}
                style={{ transform: `translate(${x - 50}px, ${y - 50}px)` }}
              >
                <g
                  className={`piece-body ${dying ? "dying" : ""} ${faded.has(p.sq) ? "faded" : ""}`}
                  style={dying ? { animationDelay: `${p.dying}ms` } : undefined}
                >
                  <circle cx={50} cy={53} r={39} className="piece-shadow" />
                  <circle cx={50} cy={50} r={39} fill={white ? "url(#pw)" : "url(#pb)"} className={white ? "pw" : "pb"} />
                  <circle cx={50} cy={50} r={29} fill="none" className={white ? "ring-w" : "ring-b"} />
                  {king && <path d="M30 60 L27 38 L39 49 L50 32 L61 49 L73 38 L70 60 Z" className={white ? "crown-w" : "crown-b"} />}
                </g>
              </g>
            );
          })}
          {arrows.map((a, i) => {
            const pts = (a.path && a.path.length > 1 ? a.path : [a.from, a.to]).map((s) => centre(s, flipped));
            return (
              <polyline
                key={`a${i}`}
                points={pts.map((p) => p.join(",")).join(" ")}
                className="arrow"
                style={{ stroke: a.color ?? "var(--accent)", strokeWidth: a.width ?? 16, opacity: a.opacity ?? 0.8 }}
                markerEnd="url(#arrowhead)"
              />
            );
          })}
        </svg>
        {typed && <div className="typed num">{typed}</div>}
      </div>
      {interactive && (
        <div className="board-hint small" role="status" aria-live="polite">
          {hintText}
        </div>
      )}
    </div>
  );
});
